/**
 * ContextInputView — Input screen for the Context Builder module.
 */
import { useState } from 'react'
import type { ContextOptions, ContextPreset } from '@/types'

interface ContextInputViewProps {
  options:        ContextOptions
  setOptions:     (v: ContextOptions) => void
  presets:        ContextPreset[]
  presetsLoading: boolean
  onSubmit:       () => void
}

type InputMode = 'preset' | 'form' | 'yaml'

export default function ContextInputView({
  options, setOptions, presets, presetsLoading, onSubmit,
}: ContextInputViewProps) {
  const [mode, setMode] = useState<InputMode>('preset')
  const [challengeInput, setChallengeInput] = useState('')

  const set = <K extends keyof ContextOptions>(key: K, value: ContextOptions[K]) =>
    setOptions({ ...options, [key]: value })

  const addChallenge = () => {
    const trimmed = challengeInput.trim()
    if (!trimmed) return
    set('challenges', [...(options.challenges ?? []), trimmed])
    setChallengeInput('')
  }

  const removeChallenge = (idx: number) => {
    set('challenges', (options.challenges ?? []).filter((_, i) => i !== idx))
  }

  const canSubmit =
    mode === 'preset' ? Boolean(options.preset) :
    mode === 'yaml'   ? Boolean(options.config_yaml?.trim()) :
    Boolean(options.company?.trim())

  return (
    <div className="flex flex-col items-center justify-start pt-14 pb-24 px-5 animate-fade-up">
      <div className="w-full max-w-[720px]">
        <div className="text-center mb-10">
          <h1 className="text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-navy-900 leading-tight">
            Build AI context<br className="hidden sm:block" /> documents.
          </h1>
          <p className="mt-3 text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Generate vector-store-ready context documents — company profiles,
            market reports, department briefs and more.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-1 mb-7 p-1 bg-slate-100 rounded-xl w-fit mx-auto">
          {(['preset', 'form', 'yaml'] as InputMode[]).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all
                ${mode === m
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'}`}
            >
              {m === 'preset' ? 'Use preset' : m === 'form' ? 'Fill form' : 'YAML config'}
            </button>
          ))}
        </div>

        {/* ── Preset selector ──────────────────────────────────────────────── */}
        {mode === 'preset' && (
          <div>
            {presetsLoading ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="skeleton rounded-xl h-24" />
                ))}
              </div>
            ) : presets.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">
                No presets found — switch to Fill form or YAML config.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {presets.map(p => (
                  <button
                    key={p.name}
                    onClick={() => set('preset', p.name)}
                    className={`preset-card ${options.preset === p.name ? 'selected' : ''}`}
                  >
                    <p className="font-semibold text-slate-900 text-sm mb-1">{p.label}</p>
                    <p className="text-xs text-slate-500 leading-snug mb-3">{p.description}</p>
                    <div className="flex gap-2">
                      <span className="tag-pill">{p.company}</span>
                      {p.product && <span className="tag-pill">{p.product}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Fill form ────────────────────────────────────────────────────── */}
        {mode === 'form' && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Company *</label>
                <input
                  type="text"
                  value={options.company ?? ''}
                  onChange={e => set('company', e.target.value)}
                  placeholder="e.g. Acme Corp"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                             text-slate-800 placeholder:text-slate-300 focus:outline-none
                             focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">
                  Product / Service <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={options.product ?? ''}
                  onChange={e => set('product', e.target.value)}
                  placeholder="e.g. Enterprise Security Suite"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                             text-slate-800 placeholder:text-slate-300 focus:outline-none
                             focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Department <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={options.department ?? ''}
                onChange={e => set('department', e.target.value)}
                placeholder="e.g. Marketing, Sales, Product"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                           text-slate-800 placeholder:text-slate-300 focus:outline-none
                           focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Challenges / focus areas
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={challengeInput}
                  onChange={e => setChallengeInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChallenge() } }}
                  placeholder="Type a challenge and press Enter"
                  className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm
                             text-slate-800 placeholder:text-slate-300 focus:outline-none
                             focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
                />
                <button
                  type="button"
                  onClick={addChallenge}
                  className="px-4 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-medium
                             hover:bg-navy-800 transition-colors"
                >
                  Add
                </button>
              </div>
              {(options.challenges ?? []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(options.challenges ?? []).map((c, i) => (
                    <span key={i} className="flex items-center gap-1.5 tag-pill pr-1">
                      {c}
                      <button
                        onClick={() => removeChallenge(i)}
                        className="ml-0.5 w-3.5 h-3.5 rounded-full bg-slate-300 hover:bg-red-400
                                   flex items-center justify-center text-white transition-colors"
                        aria-label={`Remove ${c}`}
                      >
                        <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                          <path d="M1 1l4 4M5 1 1 5" stroke="currentColor" strokeWidth="1.2"
                                strokeLinecap="round"/>
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── YAML config ──────────────────────────────────────────────────── */}
        {mode === 'yaml' && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Configuration YAML
            </label>
            <textarea
              value={options.config_yaml ?? ''}
              onChange={e => set('config_yaml', e.target.value)}
              placeholder={"company: Acme Corp\nproduct: Security Suite\ndepartment: Marketing\nchallenges:\n  - competitive differentiation\n  - enterprise adoption"}
              className="w-full resize-none rounded-2xl border-2 border-slate-200 px-5 py-4 text-sm font-mono
                         leading-relaxed text-slate-800 placeholder:text-slate-300 bg-white shadow-card
                         focus:outline-none focus:border-brand focus:ring-2 focus:ring-blue-100 transition-all"
              style={{ minHeight: 200 }}
            />
          </div>
        )}

        {/* Run button */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={`w-full sm:w-auto sm:min-w-[220px] flex items-center justify-center gap-2
              px-8 py-4 rounded-2xl text-[15px] font-semibold text-white
              transition-all duration-200 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-brand focus-visible:ring-offset-2
              ${canSubmit
                ? 'bg-navy-900 hover:bg-navy-800 shadow-md hover:shadow-lg active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Build context documents
          </button>
        </div>
      </div>
    </div>
  )
}
